import {
  AfterEffect,
  AfterEffectWithFilter,
  AfterEffectWithFilterAndMask,
  AfterEffectWithMask,
  BendingTextProperties,
  Configuration,
  DesignConfiguration,
  DesignConfigurationImageProperties,
  DesignProperties,
  PrintTypesMap,
  Product,
  SharingDraft,
  SharingDraftConfiguration,
  SharingDraftDesignConfiguration,
  SpecialTextProperties,
  StoreFontFamily,
  TextConfiguration,
  TextConfigurationContent,
} from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { assertNever } from '@/utils/assertNever';
import { getTextContent } from './utils/getTextContent';
import { getTextCoordinates } from './utils/getTextCoordinates';
import { getTextPrintColor } from './utils/getTextPrintColor';

export const isDesignConfiguration = (
  configuration: Configuration
): configuration is DesignConfiguration => configuration?.type === 'design';

const isTextConfiguration = (configuration: Configuration): configuration is TextConfiguration =>
  configuration?.type === 'text';

const isSpecialTextProperties = (
  properties: DesignConfiguration['properties']
): properties is SpecialTextProperties =>
  Boolean((properties as SpecialTextProperties)?.specialText);

const isBendingTextProperties = (
  properties: DesignConfiguration['properties']
): properties is BendingTextProperties =>
  (properties as BendingTextProperties)?.type === 'bendingText';

const isDesignProperties = (
  properties: DesignConfiguration['properties']
): properties is DesignProperties => (properties as DesignProperties)?.type === 'afterEffect';

const isDesignPropertiesWithFilter = (
  afterEffect: AfterEffect
): afterEffect is AfterEffectWithFilter | AfterEffectWithFilterAndMask => 'filter' in afterEffect;

const isDesignPropertiesWithMask = (
  afterEffect: AfterEffect
): afterEffect is AfterEffectWithMask | AfterEffectWithFilterAndMask => 'id' in afterEffect;

const getPrintColors = (image: DesignConfigurationImageProperties) => {
  // draft only knows printColorId but does work with multiple ids
  const { printColorRGBs, printColorIds: printColorId } = image;
  if (printColorId) {
    return { printColorId };
  }

  if (printColorRGBs) {
    return { printColorRGBs };
  }

  return {};
};

const mapConfigurationToSharingDraftConfiguration = (
  configuration: Configuration,
  printTypes: PrintTypesMap,
  fontFamilies: StoreFontFamily[]
): SharingDraftConfiguration => {
  const {
    offset,
    printArea: { id: printAreaId },
    printType: { id: printTypeId },
  } = configuration;

  if (isTextConfiguration(configuration)) {
    const text = configuration.content.svg.text;
    const textCoordinates = getTextCoordinates(text);

    return {
      type: 'text',
      offset,
      // width and height is not needed for text (placeholder is not a valid type in a product)
      width: 0,
      height: 0,
      transform: text.transform,
      printAreaId,
      printTypeId,
      text: getTextContent(text.content),
      ...getTextPrintColor(text),
      fontFamilyId: text.fontFamilyId,
      fontId: text.fontId,
      fontSize: parseFloat(text.fontSize),
      textAnchor:
        (text.content[0] as TextConfigurationContent)?.textAnchor || text.textAnchor || 'middle',
      x: textCoordinates.x,
      y: textCoordinates.y,
    };
  }

  if (isDesignConfiguration(configuration)) {
    const image = configuration.content.svg.image;
    const sharingDraftConfigurationBase = {
      offset,
      width: image.width,
      height: image.height,
      transform: image.transform,
      printAreaId,
      printTypeId,
    };

    const properties = configuration.properties;
    if (isSpecialTextProperties(properties)) {
      const { alignment, fontId, text } = properties;
      return {
        type: 'specialText',
        ...sharingDraftConfigurationBase,
        text,
        fontId: String(fontId),
        textAlign: alignment,
      };
    }

    if (isBendingTextProperties(properties)) {
      const { angle, fontFamilyId, fontSize, fontStyle, fontWeight, text } = properties;
      const fontFamily = assertDefined(
        fontFamilies.find((fontFamily) => fontFamily.id === fontFamilyId)
      );
      const font = assertDefined(
        fontFamily.fonts.find((font) => font.style === fontStyle && font.weight === fontWeight)
      );
      return {
        type: 'bendingText',
        ...sharingDraftConfigurationBase,
        text,
        ...getPrintColors(image),
        fontFamilyId,
        fontId: font.id,
        fontSize,
        angle,
      };
    }

    const designId = image.designId;

    const sharingDraftDesignConfiguration: SharingDraftDesignConfiguration = {
      type: 'design',
      ...sharingDraftConfigurationBase,
      designId,
      ...getPrintColors(image),
    };

    if (isDesignProperties(properties)) {
      const designProperties = properties.afterEffect;

      const { id: originalDesignId } = designProperties.originalDesign;
      sharingDraftDesignConfiguration.productDesignId = sharingDraftDesignConfiguration.designId;
      sharingDraftDesignConfiguration.designId = originalDesignId;

      if (isDesignPropertiesWithFilter(designProperties)) {
        sharingDraftDesignConfiguration.filter = designProperties.filter;
      }

      if (isDesignPropertiesWithMask(designProperties)) {
        sharingDraftDesignConfiguration.mask = {
          id: designProperties.id,
          offset: designProperties.offset,
          scale: designProperties.scale,
          version: designProperties.version,
        };
      }
    }

    return sharingDraftDesignConfiguration;
  }

  assertNever(configuration);
};

export const mapProductToSharingDraft = (
  product: Product,
  printTypes: PrintTypesMap,
  fonts: StoreFontFamily[]
): SharingDraft => {
  const {
    productType: { id: productTypeId },
    appearance: { id: appearanceId },
    defaultValues,
  } = product;

  return {
    productTypeId,
    appearanceId,
    viewId: defaultValues?.defaultView.id,
    configurations: product.configurations.map((configuration) =>
      mapConfigurationToSharingDraftConfiguration(configuration, printTypes, fonts)
    ),
    product:
      product.id && product.href
        ? {
            id: product.id,
            href: product.href,
            lookupId: product.lookupId,
          }
        : undefined,
  };
};
